import React, { useEffect, useState } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useIntegrationStatus } from '../../contexts/IntegrationStatusContext';

interface OrgGitHubStatus {
  connected: boolean;
  github_username?: string;
  github_email?: string;
  scopes: string[];
  connected_at?: string;
  avatar_url?: string;
  manage_url?: string;
  connected_by?: string;
}

interface OrgGitHubConnectionProps {
  organizationId: string;
  readOnly?: boolean;
}

const OrgGitHubConnection: React.FC<OrgGitHubConnectionProps> = ({ organizationId, readOnly = false }) => {
  const api = useAuthenticatedApi();
  const { refresh: refreshIntegrationStatus } = useIntegrationStatus();
  const [status, setStatus] = useState<OrgGitHubStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = async () => {
    try {
      const res = await api.get(`/organizations/${organizationId}/github/status`);
      setStatus(res);
    } catch (e) {
      console.error('Failed to fetch org GitHub status', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [organizationId]);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await api.get(`/organizations/${organizationId}/github/connect`);
      window.location.href = res.authorization_url;
    } catch (e) {
      console.error('Failed to initiate org GitHub connection', e);
      setConnecting(false);
    }
  };

  const confirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.delete(`/organizations/${organizationId}/github/disconnect`);
      refreshIntegrationStatus();
      await refresh();
      setShowDisconnectModal(false);
    } catch (e) {
      console.error('Failed to disconnect org GitHub', e);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-ui-background-tertiary rounded w-3/4 mb-3"></div>
        <div className="h-10 bg-ui-background-tertiary rounded w-32"></div>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-ui-text-secondary">
          Connect a GitHub account to your organization to enable repository imports,
          commit changes, and pull request creation for all members.
        </div>
        <button
          onClick={connect}
          disabled={connecting || readOnly}
          className="px-4 py-2 rounded-lg transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed bg-brand-primary hover:bg-brand-primaryDark text-black"
        >
          {connecting ? 'Connecting…' : 'Connect GitHub'}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {status.avatar_url && (
              <img
                src={status.avatar_url}
                alt="avatar"
                className="w-10 h-10 rounded-full border-2 border-ui-border-primary"
              />
            )}
            <div>
              <div className="font-medium text-ui-text-primary">
                @{status.github_username}
              </div>
              {status.github_email && (
                <div className="text-xs text-ui-text-secondary">
                  {status.github_email}
                </div>
              )}
              <div className="text-xs text-ui-text-secondary">
                Scopes: {(status.scopes || []).join(', ') || 'none'}
              </div>
            </div>
          </div>
          {status.manage_url ? (
            <a
              href={status.manage_url}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded-lg bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors text-sm font-medium"
            >
              Manage on GitHub
            </a>
          ) : (
            <button
              onClick={() => setShowDisconnectModal(true)}
              disabled={readOnly}
              className="px-3 py-2 rounded-lg transition-colors text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-red-100 dark:disabled:hover:bg-red-900/30"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>

      {showDisconnectModal && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !disconnecting && setShowDisconnectModal(false)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md bg-ui-background-secondary rounded-xl shadow-2xl border border-ui-border-primary overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-ui-border-primary">
                <h3 className="text-base font-semibold text-ui-text-primary">
                  Disconnect GitHub
                </h3>
              </div>
              <div className="px-5 py-4 text-sm text-ui-text-secondary">
                Are you sure you want to disconnect GitHub from this organization?
                All members will lose access to GitHub features until reconnected.
              </div>
              <div className="px-5 py-4 flex justify-end gap-2 border-t border-ui-border-primary">
                <button
                  className="px-4 py-2 rounded-lg bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() => setShowDisconnectModal(false)}
                  disabled={disconnecting}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={confirmDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrgGitHubConnection;
